import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useGetAllCompaniesQuery } from '../../store/api/superAdminApi';
import { formatCurrency, formatDate } from '../../utils/formatters';

const SubscriptionStatusDebugger = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { data: companiesData, isLoading, error } = useGetAllCompaniesQuery({
    page: 1,
    limit: 100 // Get more companies for the dropdown
  });

  // Safely extract companies array from the API response structure
  // API returns: { success: true, data: { companies: [...], pagination: {...} } }
  const companies = Array.isArray(companiesData?.data?.companies) 
    ? companiesData.data.companies 
    : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'trial':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending_upgrade':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'canceled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'trial':
        return <ClockIcon className="w-4 h-4" />;
      case 'pending_upgrade':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'expired':
        return <XMarkIcon className="w-4 h-4" />;
      case 'canceled':
        return <XMarkIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <div className="text-red-800">
          <p className="font-medium">Error loading companies:</p>
          <p className="text-sm mt-1">{error.message || 'Unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(companies) || companies.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <div className="text-yellow-800">
          <p className="font-medium">No companies available</p>
          <p className="text-sm mt-1">No companies found or data format is incorrect.</p>
          {companiesData && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs">Debug info</summary>
              <pre className="text-xs mt-1 bg-yellow-100 p-2 rounded overflow-auto">
                {JSON.stringify(companiesData, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center mb-6">
        <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold text-gray-900">Subscription Status Monitor</h2>
      </div>

      <div className="space-y-6">
        {/* Company Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Company to Monitor
          </label>
          <select
            value={selectedCompany?._id || ''}
            onChange={(e) => {
              const company = companies.find(c => c._id === e.target.value);
              setSelectedCompany(company);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a company...</option>
            {companies.map((company) => (
              <option key={company._id} value={company._id}>
                {company.name} ({company.email || 'No email'})
              </option>
            ))}
          </select>
        </div>

        {/* Company Subscription Details */}
        {selectedCompany && selectedCompany.subscription && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-gray-200 rounded-lg p-4 bg-gray-50"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Subscription Details for {selectedCompany.name}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Subscription Status */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(selectedCompany.subscription.status)}`}>
                    {getStatusIcon(selectedCompany.subscription.status)}
                    <span className="ml-1 capitalize">{selectedCompany.subscription.status}</span>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Payment Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(selectedCompany.subscription.paymentStatus)}`}>
                    {selectedCompany.subscription.paymentStatus}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Plan:</span>
                  <span className="text-sm text-gray-900 font-medium">
                    {selectedCompany.subscription.planId?.name || 'No Plan'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Price:</span>
                  <span className="text-sm text-gray-900 font-medium">
                    {selectedCompany.subscription.price ? 
                      formatCurrency(selectedCompany.subscription.price, selectedCompany.subscription.currency || 'USD') :
                      'Free'
                    }
                    {selectedCompany.subscription.billingCycle && ` / ${selectedCompany.subscription.billingCycle}`}
                  </span>
                </div>
              </div>

              {/* Dates and Limits */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Start Date:</span>
                  <span className="text-sm text-gray-900">
                    {selectedCompany.subscription.startDate ? formatDate(selectedCompany.subscription.startDate) : 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">End Date:</span>
                  <span className="text-sm text-gray-900">
                    {selectedCompany.subscription.endDate ? formatDate(selectedCompany.subscription.endDate) : 'N/A'}
                  </span>
                </div>

                {selectedCompany.subscription.trialEndDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Trial End:</span>
                    <span className="text-sm text-gray-900">
                      {formatDate(selectedCompany.subscription.trialEndDate)}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Next Billing:</span>
                  <span className="text-sm text-gray-900">
                    {selectedCompany.subscription.nextBillingDate ? formatDate(selectedCompany.subscription.nextBillingDate) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Limits */}
            {selectedCompany.subscription.limits && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Subscription Limits</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-blue-600">
                      {selectedCompany.subscription.limits.maxEmployees || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Max Employees</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-green-600">
                      {selectedCompany.subscription.limits.maxBranches || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Max Branches</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-purple-600">
                      {selectedCompany.subscription.limits.storage || 'N/A'}GB
                    </div>
                    <div className="text-xs text-gray-500">Storage</div>
                  </div>
                </div>
              </div>
            )}

            {/* Current Usage */}
            {selectedCompany.subscription.currentUsage && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Usage</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-blue-600">
                      {selectedCompany.subscription.currentUsage.employees || 0}
                    </div>
                    <div className="text-xs text-gray-500">Employees</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-green-600">
                      {selectedCompany.subscription.currentUsage.branches || 0}
                    </div>
                    <div className="text-xs text-gray-500">Branches</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-purple-600">
                      {selectedCompany.subscription.currentUsage.storage || 0}GB
                    </div>
                    <div className="text-xs text-gray-500">Storage Used</div>
                  </div>
                </div>
              </div>
            )}

            {/* Pending Upgrade Info */}
            {selectedCompany.subscription.pendingUpgrade && (
              <div className="mt-4 pt-4 border-t border-yellow-200 bg-yellow-50 rounded-lg p-3">
                <h4 className="text-sm font-medium text-yellow-900 mb-2 flex items-center">
                  <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                  Pending Upgrade
                </h4>
                <div className="text-sm text-yellow-800">
                  <p>Upgrade requested: {formatDate(selectedCompany.subscription.pendingUpgrade.requestedAt)}</p>
                  <p>New Plan: {selectedCompany.subscription.pendingUpgrade.planId?.name || 'Unknown'}</p>
                  <p>Billing Cycle: {selectedCompany.subscription.pendingUpgrade.billingCycle}</p>
                </div>
              </div>
            )}

            {/* Workflow Status Indicators */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Workflow Status</h4>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  {selectedCompany.subscription.status === 'trial' ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                  ) : (
                    <XMarkIcon className="w-4 h-4 text-gray-400 mr-2" />
                  )}
                  <span className={selectedCompany.subscription.status === 'trial' ? 'text-green-700' : 'text-gray-600'}>
                    Trial Mode: {selectedCompany.subscription.status === 'trial' ? 'Active' : 'Not Active'}
                  </span>
                </div>

                <div className="flex items-center text-sm">
                  {selectedCompany.subscription.status === 'active' && selectedCompany.subscription.paymentStatus === 'paid' ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                  ) : (
                    <XMarkIcon className="w-4 h-4 text-gray-400 mr-2" />
                  )}
                  <span className={selectedCompany.subscription.status === 'active' && selectedCompany.subscription.paymentStatus === 'paid' ? 'text-green-700' : 'text-gray-600'}>
                    Paid Subscription: {selectedCompany.subscription.status === 'active' && selectedCompany.subscription.paymentStatus === 'paid' ? 'Active' : 'Not Active'}
                  </span>
                </div>

                <div className="flex items-center text-sm">
                  {selectedCompany.subscription.status === 'pending_upgrade' ? (
                    <ClockIcon className="w-4 h-4 text-yellow-500 mr-2" />
                  ) : (
                    <XMarkIcon className="w-4 h-4 text-gray-400 mr-2" />
                  )}
                  <span className={selectedCompany.subscription.status === 'pending_upgrade' ? 'text-yellow-700' : 'text-gray-600'}>
                    Pending Upgrade: {selectedCompany.subscription.status === 'pending_upgrade' ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {selectedCompany && !selectedCompany.subscription && (
          <div className="text-center py-8 text-gray-500">
            <InformationCircleIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p>No subscription found for {selectedCompany.name}</p>
          </div>
        )}

        {!selectedCompany && (
          <div className="text-center py-8 text-gray-500">
            <InformationCircleIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p>Select a company to view subscription details</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SubscriptionStatusDebugger;